import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { endOfMonth, startOfMonth } from 'date-fns';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import GeneralStatistics from './components/GeneralStatistics';
import EmployeeAnalytics from './components/EmployeeAnalytics';
import CallDetails from './components/CallDetails';
import MissedCalls from './components/MissedCalls';
import Clients from './components/Clients';
import Complaints from './components/Complaints';
import EmployeeEvaluation from './components/EmployeeEvaluation';
import Integrations from './components/Integrations';
import Settings from './components/Settings';
import Login from './components/Login';
import ExportJobsPanel, { ExportJobsLauncher } from './components/exports/ExportJobsPanel';
import { ExportJobsProvider } from './components/exports/ExportJobsContext';
import { Toaster } from './components/ui/toaster';
import { TimeZoneProvider } from './contexts/TimeZoneContext';
import {
  getStoredAuthToken,
  getStoredAuthUser,
  persistAuthToken,
  persistAuthUser
} from './config/auth';
import { APP_TAB_IDS } from './config/navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') ?? '';
const API = `${BACKEND_URL}/api`;

const isLegacyPermissions = (permissions) =>
  permissions
  && !Array.isArray(permissions.allowedTabs)
  && (
    Object.prototype.hasOwnProperty.call(permissions, 'canViewClients')
    || Object.prototype.hasOwnProperty.call(permissions, 'canManageMetadata')
    || Object.prototype.hasOwnProperty.call(permissions, 'canManagePrompts')
  );

const getAllowedTabs = (authenticatedUser) =>
  Array.isArray(authenticatedUser?.permissions?.allowedTabs)
    ? authenticatedUser.permissions.allowedTabs
    : [];

const canUserAccess = (authenticatedUser, tabId) =>
  authenticatedUser?.role === 'admin' || getAllowedTabs(authenticatedUser).includes(tabId);

const getDefaultSectionForUser = (authenticatedUser) =>
  APP_TAB_IDS.find((tabId) => canUserAccess(authenticatedUser, tabId)) ?? 'dashboard';

const getPreferredSectionForUser = (authenticatedUser, requestedSection) =>
  requestedSection && canUserAccess(authenticatedUser, requestedSection)
    ? requestedSection
    : getDefaultSectionForUser(authenticatedUser);

const CallCenterApp = () => {
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(() => {
    const section = searchParams.get('section');
    return section || 'dashboard';
  });
  const [initialCallId, setInitialCallId] = useState(() => searchParams.get('callId'));
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(getStoredAuthToken()));
  const [user, setUser] = useState(() => getStoredAuthUser());
  const [dateRange, setDateRange] = useState(() => ({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  }));
  const [callType, setCallType] = useState('all');
  const allowedTabs = useMemo(() => getAllowedTabs(user), [user]);
  const canAccess = useCallback(
    (tabId) => user?.role === 'admin' || allowedTabs.includes(tabId),
    [allowedTabs, user?.role]
  );
  const defaultSection = useMemo(() => getDefaultSectionForUser(user), [user]);

  const checkBackendHealth = async () => {
    const backendBase = BACKEND_URL?.replace(/\/$/, '');

    if (!backendBase) {
      console.warn('Backend URL is not configured; skipping health check.');
      return;
    }

    try {
      const response = await axios.get(`${backendBase}/healthz`);
      console.log('Backend health:', response.data?.status ?? 'ok');
    } catch (e) {
      console.error('Backend health check failed', e);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      checkBackendHealth();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const onUnauthorized = () => handleLogout();
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  useEffect(() => {
    if (isAuthenticated && (!user || isLegacyPermissions(user.permissions))) {
      handleLogout();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user && !canAccess(activeSection)) {
      setActiveSection(defaultSection);
    }
  }, [activeSection, canAccess, defaultSection, isAuthenticated, user]);

  const handleLoginSuccess = (token, authenticatedUser) => {
    persistAuthToken(token);
    persistAuthUser(authenticatedUser);
    setUser(authenticatedUser);
    setIsAuthenticated(true);
    setActiveSection(getPreferredSectionForUser(authenticatedUser, searchParams.get('section')));
  };

  const handleLogout = () => {
    persistAuthToken(null);
    persistAuthUser(null);
    setUser(null);
    setIsAuthenticated(false);
    setActiveSection('dashboard');
  };

  const handleInitialCallHandled = useCallback(() => {
    setInitialCallId(null);
  }, []);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <GeneralStatistics dateRange={dateRange} callType={callType} />;
      case 'employees':
        return <EmployeeAnalytics dateRange={dateRange} />;
      case 'calls':
        return (
          <CallDetails
            dateRange={dateRange}
            initialCallId={initialCallId}
            onInitialCallHandled={handleInitialCallHandled}
          />
        );
      case 'missed':
        return <MissedCalls />;
      case 'evaluation':
        return <EmployeeEvaluation />;
      case 'clients':
        return canAccess('clients') ? <Clients dateRange={dateRange} /> : <GeneralStatistics dateRange={dateRange} callType={callType} />;
      case 'complaints':
        return canAccess('complaints') ? <Complaints dateRange={dateRange} /> : <GeneralStatistics dateRange={dateRange} callType={callType} />;
      case 'integrations':
        return <Integrations dateRange={dateRange} />;
      case 'settings':
        return <Settings user={user} />;
      default:
        return <GeneralStatistics dateRange={dateRange} callType={callType} />;
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <Login apiBaseUrl={API} onSuccess={handleLoginSuccess} />
        </div>
        <Toaster />
      </>
    );
  }

  return (
    <ExportJobsProvider>
      <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
        <Sidebar
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          onLogout={handleLogout}
          user={user}
          canAccess={canAccess}
        />
        
        <main className="flex-1 min-h-screen overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="flex flex-col">
            {activeSection !== 'settings' && activeSection !== 'missed' && (
              <Dashboard
                activeSection={activeSection}
                onSectionChange={setActiveSection}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                callType={callType}
                onCallTypeChange={setCallType}
              />
            )}

            <div className="px-6 pb-8">
              {renderActiveSection()}
            </div>
          </div>
        </main>
      </div>
      <ExportJobsLauncher />
      <ExportJobsPanel />
      <Toaster />
    </ExportJobsProvider>
  );
};

function App() {
  return (
    <div className="App">
      <TimeZoneProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<CallCenterApp />} />
          </Routes>
        </BrowserRouter>
      </TimeZoneProvider>
    </div>
  );
}

export default App;
